import { useState } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { useUsageHistory, type ModelStats, type DailyUsage, type OrgUsage } from '../../hooks/useUsage';
import { fmt, fmtCost } from '../../utils/format';
import TokenUsageChart from '../charts/TokenUsageChart';
import CostBreakdownChart from '../charts/CostBreakdownChart';

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-app-surface border border-app-border rounded-xl p-5 shadow-sm">
      <p className="text-xs text-app-text-dim uppercase tracking-wider mb-1">{label}</p>
      <p className="text-2xl font-semibold text-app-text">{value}</p>
      {sub && <p className="text-xs text-app-text-dim mt-1">{sub}</p>}
    </div>
  );
}

function ModelRow({ model, stats }: { model: string; stats: ModelStats }) {
  const total = stats.input_tokens + stats.output_tokens;
  const inputPct = total > 0 ? (stats.input_tokens / total) * 100 : 0;
  const outputPct = total > 0 ? (stats.output_tokens / total) * 100 : 0;

  return (
    <div className="py-4 border-b border-app-border/40 last:border-0">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-app-text font-mono truncate">{model}</p>
          <div className="mt-2 flex gap-4 text-xs text-app-text-dim">
            <span>
              <span className="text-app-text font-medium">{fmt(stats.calls)}</span> calls
            </span>
            <span>
              <span className="text-app-text font-medium">{fmt(stats.input_tokens)}</span> in
            </span>
            <span>
              <span className="text-app-text font-medium">{fmt(stats.output_tokens)}</span> out
            </span>
            <span>
              <span className="text-app-text font-medium">{fmt(total)}</span> total tokens
            </span>
          </div>
          {/* Token ratio bar */}
          <div className="mt-2 flex h-1.5 rounded-full overflow-hidden gap-px">
            <div
              className="bg-app-accent/70 rounded-l-full"
              style={{ width: `${inputPct}%` }}
              title={`Input ${inputPct.toFixed(1)}%`}
            />
            <div
              className="bg-app-warning/70 rounded-r-full"
              style={{ width: `${outputPct}%` }}
              title={`Output ${outputPct.toFixed(1)}%`}
            />
          </div>
          <div className="mt-1 flex gap-3 text-[10px] text-app-text-dim">
            <span className="flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-sm bg-app-accent/70" />
              Input {inputPct.toFixed(0)}%
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-sm bg-app-warning/70" />
              Output {outputPct.toFixed(0)}%
            </span>
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className="text-base font-semibold text-app-text">{fmtCost(stats.cost_usd)}</p>
          <p className="text-[10px] text-app-text-dim mt-0.5">estimated cost</p>
        </div>
      </div>
    </div>
  );
}

function DailyRow({ row }: { row: DailyUsage }) {
  return (
    <tr className="border-b border-app-border/30 last:border-0">
      <td className="py-3 pr-4 text-sm text-app-text">{row.date}</td>
      <td className="py-3 pr-4 text-sm text-app-text text-right">{fmt(row.calls)}</td>
      <td className="py-3 pr-4 text-sm text-app-text text-right">{fmt(row.tokens)}</td>
      <td className="py-3 text-sm text-app-text text-right font-medium">{fmtCost(row.cost_usd)}</td>
    </tr>
  );
}

function OrgRow({ row }: { row: OrgUsage }) {
  return (
    <tr className="border-b border-app-border/30 last:border-0">
      <td className="py-3 pr-4 text-sm text-app-text font-medium">{row.org_name}</td>
      <td className="py-3 pr-4 text-sm text-app-text text-right">{fmt(row.calls)}</td>
      <td className="py-3 pr-4 text-sm text-app-text text-right">{fmt(row.tokens)}</td>
      <td className="py-3 text-sm text-app-text text-right">{fmtCost(row.cost_usd)}</td>
    </tr>
  );
}

const DAYS_OPTIONS = [1, 7, 14, 30];

export default function HistoryTab() {
  const [days, setDays] = useState(7);
  const [orgFilter, setOrgFilter] = useState('');
  const { data, loading, error, refresh } = useUsageHistory(days, orgFilter);

  const totalTokens = data?.total_tokens ?? 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm text-app-text-dim mr-2">Period:</p>
          {DAYS_OPTIONS.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              className={`px-3 py-1.5 rounded-lg text-sm border ${
                days === d
                  ? 'bg-app-accent/10 border-app-accent text-app-accent font-medium'
                  : 'border-app-border text-app-text-dim hover:bg-app-muted'
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-sm text-app-text-dim shrink-0" htmlFor="usage-org-filter">
            Org:
          </label>
          <input
            id="usage-org-filter"
            type="text"
            value={orgFilter}
            onChange={(e) => setOrgFilter(e.target.value)}
            placeholder="e.g. your-station (optional)"
            className="min-w-[10rem] max-w-xs rounded-lg border border-app-border bg-app-surface px-3 py-1.5 text-sm text-app-text placeholder:text-app-text-dim/60"
          />
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-app-accent border border-app-border hover:bg-app-muted disabled:opacity-50"
        >
          <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {loading && !data && (
        <p className="text-sm text-app-text-dim py-8 text-center">Loading...</p>
      )}

      {error && (
        <div className="bg-app-error/10 border border-app-error/30 rounded-xl p-4 text-sm text-app-error">
          {error}
        </div>
      )}

      {data?.error && (
        <div className="bg-app-error/10 border border-app-error/30 rounded-xl p-4 text-sm text-app-error">
          {data.error}
        </div>
      )}

      {data && !data.error && (
        <>
          {data.filter_org_name != null && data.filter_org_name !== '' && (
            <p className="text-xs text-app-accent">
              กรองเฉพาะ org: <span className="font-mono">{data.filter_org_name}</span>
            </p>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Total cost" value={fmtCost(data.total_cost_usd ?? 0)} sub={`last ${days} day(s)`} />
            <StatCard
              label="LLM calls"
              value={fmt(data.total_calls ?? 0)}
              sub={`${Object.keys(data.by_model ?? {}).length} model(s)`}
            />
            <StatCard label="Total tokens" value={fmt(totalTokens)} />
            <StatCard
              label="Avg cost/day"
              value={fmtCost(days > 0 ? (data.total_cost_usd ?? 0) / days : 0)}
              sub="estimated"
            />
          </div>

          {/* Daily usage chart */}
          {(data.daily ?? []).length > 0 && (
            <div className="bg-app-surface border border-app-border rounded-xl p-5 shadow-sm">
              <h2 className="text-sm font-medium text-app-text mb-3">Daily usage trend</h2>
              <TokenUsageChart data={data.daily ?? []} />
            </div>
          )}

          {/* Cost by model chart */}
          {Object.keys(data.by_model ?? {}).length > 0 && (
            <div className="bg-app-surface border border-app-border rounded-xl p-5 shadow-sm">
              <h2 className="text-sm font-medium text-app-text mb-3">Cost by model</h2>
              <CostBreakdownChart data={data.by_model ?? {}} />
            </div>
          )}

          {data.by_org && data.by_org.length > 0 && (
            <div className="bg-app-surface border border-app-border rounded-xl p-5 shadow-sm">
              <h2 className="text-sm font-medium text-app-text mb-1">By organization</h2>
              <p className="text-xs text-app-text-dim mb-4">
                สรุปทุก org ในช่วงเวลา (ไม่เปลี่ยนตามช่องกรอง Org — ใช้กรองเฉพาะตารางด้านล่างและแท็บ Log)
              </p>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-app-border text-xs text-app-text-dim uppercase tracking-wider">
                    <th className="py-2 pr-4 text-left font-medium">Organization</th>
                    <th className="py-2 pr-4 text-right font-medium">Calls</th>
                    <th className="py-2 pr-4 text-right font-medium">Tokens</th>
                    <th className="py-2 text-right font-medium">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_org.map((row) => (
                    <OrgRow key={row.org_name} row={row} />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Daily breakdown table */}
          {(data.daily ?? []).length > 0 && (
            <div className="bg-app-surface border border-app-border rounded-xl p-5 shadow-sm">
              <h2 className="text-sm font-medium text-app-text mb-4">Daily breakdown</h2>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-app-border text-xs text-app-text-dim uppercase tracking-wider">
                    <th className="py-2 pr-4 text-left font-medium">Date</th>
                    <th className="py-2 pr-4 text-right font-medium">Calls</th>
                    <th className="py-2 pr-4 text-right font-medium">Tokens</th>
                    <th className="py-2 text-right font-medium">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.daily ?? []).map((row) => (
                    <DailyRow key={row.date} row={row} />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* By model */}
          <div className="bg-app-surface border border-app-border rounded-xl p-5">
            <h2 className="text-sm font-medium text-app-text mb-4">Breakdown by model</h2>
            {Object.keys(data.by_model ?? {}).length === 0 ? (
              <p className="text-sm text-app-text-dim py-4">ไม่มีข้อมูลในช่วงนี้</p>
            ) : (
              <div>
                {Object.entries(data.by_model ?? {})
                  .sort((a, b) => b[1].cost_usd - a[1].cost_usd)
                  .map(([model, stats]) => (
                    <ModelRow key={model} model={model} stats={stats} />
                  ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
